import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { LSSEntityObject } from '../../shared/list/lssEntity.datamodel';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  DeploymentEntity,
  DeploymentLevel,
  DeploymentLevelCategory,
  DeploymentSection
} from '../store/deployment.datamodel';
import { DeploymentService } from '../store/deployment.service';
import { AppObject } from '../../application/app.datamodel';
import {PermissionsType} from '../../user/permissions/permissions.datamodel';

@Component({
    selector: 'ama-ng-app-store-deployment-list',
    templateUrl: './deployment-list.component.html',
    styleUrls: ['./deployment-list.component.scss'],
    standalone: false
})
export class DeploymentListComponent implements OnInit, OnChanges {
  @Input() app!: AppObject;
  @Input() section!: DeploymentSection;
  @Input() deploymentLevel!: DeploymentLevelCategory;
  @Input() editConfiguration = false;
  @Input() label!: string;
  @Input() itemsList!: DeploymentEntity[];
  @Input() entitySelected!: LSSEntityObject;
  @Input() isConfigurationAvailable = false;
  @Output() selectionChanged = new EventEmitter();
  @Output() editConfigurationEvent = new EventEmitter<DeploymentEntity[]>();
  itemsGroup!: UntypedFormGroup;
  FORM_KEY = "checkboxArray";
  isUserAllowToDeploy = false;
  protected readonly PermissionsType = PermissionsType;

  constructor(private readonly formBuilder: UntypedFormBuilder, public readonly deploymentService: DeploymentService) {
  }

  get selectAllChecked() {
    return (
      this.itemsGroup.get(this.FORM_KEY)?.value.length > 0 &&
      this.itemsGroup.get(this.FORM_KEY)?.value.every((selected: boolean) => selected)
    );
  }

  get selectAllIndeterminate() {
    return (
      !this.selectAllChecked &&
      this.itemsGroup.get(this.FORM_KEY)?.value.some((selected: boolean) => selected)
    );
  }

  ngOnInit() {
    this.deploymentService.isUserAllowToDeploy$.subscribe(isUserAllowToDeploy => {
      this.isUserAllowToDeploy = isUserAllowToDeploy;
      if (this.isUserAllowToDeploy) {
        this.itemsGroup.get(this.FORM_KEY)?.enable();
      } else {
        this.itemsGroup.get(this.FORM_KEY)?.disable();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.itemsList) {
      this.itemsGroup = this.formBuilder.group({
        [this.FORM_KEY]: this.formBuilder.array(this.itemsList?.map(entity => entity.isSelected))
      });
      if (!this.isUserAllowToDeploy) {
        this.itemsGroup.get(this.FORM_KEY)?.disable();
      }
    }
  }

  /**
   * Handle the selection of the 'Select all' checkbox
   * @param $event
   */
  onSelectAllCheckboxChange($event: any) {
    if (this.section === DeploymentSection.UNDEPLOY) {
      $event.target.checked ?
        this.deploymentService.dispatchSelectEntitiesToBeDeployed(this.app.id!, [...this.itemsList]) :
        this.deploymentService.dispatchUnselectEntitiesToBeDeployed(this.app.id!, [...this.itemsList]);
    } else {
      $event.target.checked ?
        this.deploymentService.dispatchSelectEntitiesToBeUndeployed(this.app.id!, [...this.itemsList]) :
        this.deploymentService.dispatchUnselectEntitiesToBeUndeployed(this.app.id!, [...this.itemsList]);
    }
  }

  /**
   * Handle the selection of an item
   * @param item
   */
  onSelectItemCheckboxChange(item: DeploymentEntity) {
    if (this.section === DeploymentSection.UNDEPLOY) {
      item.isSelected ?
        this.deploymentService.dispatchUnselectEntitiesToBeDeployed(this.app.id!, item) :
        this.deploymentService.dispatchSelectEntitiesToBeDeployed(this.app.id!, item);
    } else {
      item.isSelected ?
        this.deploymentService.dispatchUnselectEntitiesToBeUndeployed(this.app.id!, item) :
        this.deploymentService.dispatchSelectEntitiesToBeUndeployed(this.app.id!, item);
    }
  }

  /**
   * Emits a true value for editing configuration
   */
  triggerEditConfiguration(): void {
    const selectedEntities = this.itemsList.filter(entity => entity.isSelected);
    if (this.deploymentLevel.level === DeploymentLevel.USER) {
      this.editConfigurationEvent.emit(
        this.deploymentService.addOriginalOrgToEntities(selectedEntities, this.entitySelected.label!)
      );
    } else {
      this.editConfigurationEvent.emit(selectedEntities);
    }
  }
}
